import { Image } from '@unistocks/components/basic/image';
import { Group } from '@unistocks/components/group/group';
import { UnistocksCircle } from '@unistocks/components/logo/unistocks-circle';
import { SyncedAssetListItem } from '@unistocks/features/portfolios/components/asset-settings/external-holdings-tab';
import { findExternalPortfolio } from '@unistocks/features/portfolios/components/asset-settings/utils';
import { formatCurrency, formatNumber, formatPercentage, t } from '@unistocks/localization';
import { PortfolioDto, SectionAssetDto } from '@unistocks/proxy/axios-client';
import { assetTypeHasTotalValue } from '@unistocks/shared/utils/asset-utils';
import { isNumber } from '@unistocks/shared/utils/util';
import { ListItem, Separator } from 'tamagui';

export type SyncedAssetDetailsProps = {
  asset: SectionAssetDto;
  syncedAsset: SyncedAssetListItem;
  portfolio: PortfolioDto;
};

export const SyncedAssetDetails: React.FC<SyncedAssetDetailsProps> = ({
  asset,
  syncedAsset,
  portfolio
}) => {
  const syncedPortfolio = findExternalPortfolio(syncedAsset.value.syncedAsset, portfolio);

  const icon = syncedAsset.image ? (
    <Image rounded size={'$2.5'} source={syncedAsset.image} />
  ) : (
    <UnistocksCircle rounded size={'$2.5'} />
  );

  return (
    <Group separator={<Separator />}>
      <Group.Item>
        <ListItem
          title={t('::AccountName')}
          subTitle={syncedAsset.value.accountName}
          icon={icon}
          gap={'$3'}
          pressStyle={{ backgroundColor: 'transparent' }}
        />
      </Group.Item>

      <Group.Item>
        <ListItem
          title={t('::Name')}
          subTitle={syncedAsset.value.syncedAsset.name}
          icon={icon}
          gap={'$3'}
          pressStyle={{ backgroundColor: 'transparent' }}
        />
      </Group.Item>

      {isNumber(syncedAsset.value.syncedAsset.quantity) ? (
        <Group.Item>
          <ListItem
            title={t('::Quantity')}
            subTitle={`${formatNumber(syncedAsset.value.syncedAsset.quantity)} ${t(
              '::QuantitySuffix'
            )}`}
            icon={icon}
            gap={'$3'}
            pressStyle={{ backgroundColor: 'transparent' }}
          />
        </Group.Item>
      ) : null}

      {isNumber(syncedAsset.value.syncedAsset.averageAcquisitionPrice?.value) ? (
        <Group.Item>
          <ListItem
            title={t('::AverageAcquisitionPrice')}
            subTitle={`${formatCurrency(
              syncedAsset.value.syncedAsset.averageAcquisitionPrice.value,
              syncedAsset.value.syncedAsset.averageAcquisitionPrice.currency
            )}`}
            icon={icon}
            gap={'$3'}
            pressStyle={{ backgroundColor: 'transparent' }}
          />
        </Group.Item>
      ) : null}

      {isNumber(syncedAsset.value.syncedAsset.averageAcquisitionPriceAssetCurrency?.value) &&
      syncedAsset.value.syncedAsset.averageAcquisitionPrice?.currency &&
      syncedAsset.value.syncedAsset.averageAcquisitionPriceAssetCurrency.currency !==
        syncedAsset.value.syncedAsset.averageAcquisitionPrice.currency ? (
        <Group.Item>
          <ListItem
            title={t('::AverageAcquisitionPrice') + ` (${syncedAsset.value.syncedAsset.currency})`}
            subTitle={`${formatCurrency(
              syncedAsset.value.syncedAsset.averageAcquisitionPriceAssetCurrency.value,
              syncedAsset.value.syncedAsset.averageAcquisitionPriceAssetCurrency.currency
            )}`}
            icon={icon}
            gap={'$3'}
            pressStyle={{ backgroundColor: 'transparent' }}
          />
        </Group.Item>
      ) : null}

      {isNumber(syncedAsset.value.syncedAsset.interestRate) ? (
        <Group.Item>
          <ListItem
            title={t('::InterestRate')}
            subTitle={formatPercentage(syncedAsset.value.syncedAsset.interestRate)}
            icon={icon}
            gap={'$3'}
            pressStyle={{ backgroundColor: 'transparent' }}
          />
        </Group.Item>
      ) : null}

      {isNumber(syncedAsset.value.syncedAsset.totalValue) && assetTypeHasTotalValue(asset.type) ? (
        <Group.Item>
          <ListItem
            title={t('::TotalValue')}
            subTitle={formatCurrency(
              syncedAsset.value.syncedAsset.totalValue ?? 0,
              syncedPortfolio!.currency
            )}
            icon={icon}
            gap={'$3'}
            pressStyle={{ backgroundColor: 'transparent' }}
          />
        </Group.Item>
      ) : null}
    </Group>
  );
};

SyncedAssetDetails.displayName = 'SyncedAssetDetails';
